import React from 'react'
import './userdashboardhomepage.css'
import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { IoCloseSharp } from "react-icons/io5"
import { FiArrowDownLeft, FiArrowUpRight, FiAlertTriangle, FiUserCheck, FiClock, FiTrendingUp, FiGift } from "react-icons/fi"
import { RiLuggageDepositLine } from "react-icons/ri"
import { BiMoneyWithdraw } from "react-icons/bi"
import Loader from '../Loader'
import Userdashboardheader from '../userdashboardheader/Userdashboardheader'
import DashboardTopbar from '../dashboardShell/DashboardTopbar'
import MarketOverviewWidget from './MarketOverviewWidget'

// Same tiers and thresholds as the Plans page (UserdashboardRanking)
const PLANS = [
    { name: 'Starter', min: 100, medal: '/silver.png' },
    { name: 'Medium', min: 11000, medal: '/gold.png' },
    { name: 'Diamond', min: 21000, medal: '/gold.png' },
]

const getCurrentPlan = (funded) => [...PLANS].reverse().find(plan => funded >= plan.min) || null
const getNextPlan = (funded) => PLANS.find(plan => funded < plan.min) || null

const formatUsd = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const Userdashboardhomepage = ({ route }) => {
    const navigate = useNavigate()
    const [userData, setUserData] = useState()
    const [loader, setLoader] = useState(false)
    const [dismissed, setDismissed] = useState({ kyc: false, balance: false })
    const [dailyTrades, setDailyTrades] = useState([])
    const today = new Date().toLocaleDateString()

    useEffect(() => {
        const getData = async () => {
            try {
                setLoader(true)
                const token = localStorage.getItem('token')
                if (!token) { navigate('/login'); return }
                const response = await fetch(`${route}/api/getData`, {
                    headers: { 'x-access-token': token, 'Content-Type': 'application/json' },
                })
                const data = await response.json()
                if (data.status === 'error') {
                    localStorage.removeItem('token')
                    navigate('/login')
                } else if (data.onboardingCompleted === false) {
                    navigate('/onboarding')
                } else {
                    setUserData(data)
                }
            } catch (error) {
                navigate('/login')
            } finally {
                setLoader(false)
            }
        }
        getData()
    }, [navigate, route])

    useEffect(() => {
        if (userData?.trades?.length > 0) {
            setDailyTrades(userData.trades.filter(t => t.date === today))
        }
    }, [userData])

    const funded = Number(userData?.funded || 0)
    const currentPlan = getCurrentPlan(funded)
    const nextPlan = getNextPlan(funded)
    const nextPlanProgress = nextPlan ? Math.min((funded / nextPlan.min) * 100, 100) : 100
    const serverNumber = (userData?.server || 'server1').replace(/\D/g, '') || '1'

    const kycApproved = userData?.kycStatus === 'approved'
    const kycProcessing = userData?.kycStatus === 'processing'
    const showKycNotice = userData && !kycApproved && !dismissed.kyc
    const showBalanceNotice = userData && funded === 0 && !dismissed.balance

    const stats = [
        { label: 'Total deposit', value: userData?.totaldeposit, icon: <RiLuggageDepositLine />, tone: 'teal' },
        { label: 'Total profit', value: userData?.totalprofit, icon: <FiTrendingUp />, tone: 'green' },
        { label: 'Total withdrawal', value: userData?.totalwithdraw, icon: <BiMoneyWithdraw />, tone: 'blue' },
        { label: 'Referral bonus', value: userData?.refBonus, icon: <FiGift />, tone: 'amber' },
    ]

    return (
        <div className="ud-layout">
            {loader && <Loader />}
            <Userdashboardheader route={route} />

            <div className="ud-main">
                {/* TOPBAR */}
                <DashboardTopbar route={route} title="Dashboard" />

                {/* CONTENT */}
                <div className="ud-content">
                    {/* ── ALERTS ── */}
                    {showKycNotice && (
                        <div className={`udh-alert ${kycProcessing ? 'info' : 'warning'}`}>
                            <span className="udh-alert-icon">{kycProcessing ? <FiClock /> : <FiUserCheck />}</span>
                            <div className="udh-alert-body">
                                <p className="udh-alert-title">{kycProcessing ? 'KYC under review' : 'KYC verification required'}</p>
                                <p className="udh-alert-text">
                                    {kycProcessing
                                        ? "We're reviewing your documents. You'll be able to withdraw once they're approved."
                                        : 'Verify your identity to unlock withdrawals.'}
                                    {!kycProcessing && <Link to="/kyc">Complete KYC</Link>}
                                </p>
                            </div>
                            <button className="udh-alert-close" aria-label="Dismiss" onClick={() => setDismissed(d => ({ ...d, kyc: true }))}>
                                <IoCloseSharp />
                            </button>
                        </div>
                    )}

                    {showBalanceNotice && (
                        <div className="udh-alert warning">
                            <span className="udh-alert-icon"><FiAlertTriangle /></span>
                            <div className="udh-alert-body">
                                <p className="udh-alert-title">Empty balance</p>
                                <p className="udh-alert-text">
                                    Make a deposit to activate a plan and start copy trading.
                                    <Link to="/fundwallet">Deposit</Link>
                                </p>
                            </div>
                            <button className="udh-alert-close" aria-label="Dismiss" onClick={() => setDismissed(d => ({ ...d, balance: true }))}>
                                <IoCloseSharp />
                            </button>
                        </div>
                    )}

                    {/* ── BALANCE HERO ── */}
                    <div className="udh-hero">
                        <div className="udh-hero-top">
                            <div className="udh-balance-block">
                                <p className="udh-balance-label">Total balance</p>
                                <div className="udh-balance-amount">
                                    <span className="udh-amount-value">{formatUsd(funded)}</span>
                                    <span className="udh-currency-tag">{userData?.accountCurrency || 'USD'}</span>
                                </div>
                            </div>
                            <div className="udh-hero-side">
                                <div className="udh-action-row">
                                    <Link to="/withdraw" className="ud-btn-ghost"><FiArrowUpRight /> Withdraw</Link>
                                    <Link to="/fundwallet" className="ud-btn-primary"><FiArrowDownLeft /> Deposit</Link>
                                </div>
                                <Link to="/ranking" className="udh-plan-pill">
                                    {currentPlan
                                        ? <img src={currentPlan.medal} alt="" className="udh-plan-medal" />
                                        : <span className="udh-plan-medal empty" />}
                                    <span>
                                        Current plan: <strong>{currentPlan ? currentPlan.name : 'None'}</strong>
                                    </span>
                                </Link>
                            </div>
                        </div>

                        <div className="udh-stats-row">
                            {stats.map(stat => (
                                <div key={stat.label} className="udh-stat-card">
                                    <div className={`udh-stat-icon ${stat.tone}`}>{stat.icon}</div>
                                    <div className="udh-stat-text">
                                        <p className="udh-stat-label">{stat.label}</p>
                                        <p className="udh-stat-value">{formatUsd(stat.value)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── MARKET + SIDE CARDS ── */}
                    <div className="udh-grid">
                        <div className="udh-market">
                            <div className="ud-section-header">
                                <h2>Market overview</h2>
                                <p>Live prices across the markets our traders cover.</p>
                            </div>
                            <div className="ud-card udh-market-card">
                                <MarketOverviewWidget />
                            </div>
                        </div>

                        <aside className="udh-side">
                            <div className="ud-card udh-side-card">
                                <p className="udh-side-eyebrow">Your current server</p>
                                <img src="/server.png" alt="" className="udh-server-img" />
                                <h3 className="udh-side-title">Server {serverNumber}</h3>
                                <span className="udh-status"><span className="udh-status-dot" /> Connected</span>
                            </div>

                            <div className="ud-card udh-side-card">
                                <p className="udh-side-eyebrow">{nextPlan ? 'Unlock next plan' : 'Top plan reached'}</p>
                                <img src={(nextPlan || currentPlan).medal} alt="" className="udh-next-medal" />
                                {nextPlan ? (
                                    <>
                                        <h3 className="udh-side-title">{nextPlan.name} plan</h3>
                                        <p className="udh-progress-figures">
                                            {formatUsd(funded)} <span>/ {formatUsd(nextPlan.min)}</span>
                                        </p>
                                        <div className="udh-progress" role="progressbar" aria-valuenow={Math.round(nextPlanProgress)} aria-valuemin={0} aria-valuemax={100}>
                                            <div className="udh-progress-fill" style={{ width: `${nextPlanProgress}%` }} />
                                        </div>
                                        <p className="udh-side-note">
                                            Deposit {formatUsd(nextPlan.min - funded)} more to unlock it.
                                        </p>
                                        <Link to="/fundwallet" className="ud-btn-primary udh-side-btn">Deposit</Link>
                                    </>
                                ) : (
                                    <>
                                        <h3 className="udh-side-title">{currentPlan.name} plan</h3>
                                        <p className="udh-side-note">You're on our highest plan.</p>
                                    </>
                                )}
                            </div>
                        </aside>
                    </div>

                    {/* ── DAILY TRADES ── */}
                    <div className="udh-trades-section">
                        <div className="ud-section-header">
                            <h2>Today's trades</h2>
                            <p>{today}</p>
                        </div>
                        {dailyTrades.length > 0 ? (
                            <div className="ud-table-wrap">
                                <table className="ud-table">
                                    <thead>
                                        <tr>
                                            <th>Pair</th>
                                            <th>Amount</th>
                                            <th>Type</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dailyTrades.map((trade, i) => (
                                            <tr key={i}>
                                                <td>{trade.pair}</td>
                                                <td>${trade.amount} USD</td>
                                                <td>
                                                    <span className={`ud-badge ${trade.tradeType === 'profit' ? 'ud-badge-profit' : 'ud-badge-loss'}`}>
                                                        {trade.tradeType}
                                                    </span>
                                                </td>
                                                <td>{trade.date}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="ud-card ud-empty">
                                <p>Your trader has not placed any trades today. Check back later.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Userdashboardhomepage
